import { cn } from '@/lib/utils'

/**
 * Small environment indicator. Renders a subtle pill for the `development` and
 * `test` stages so it's obvious which environment you're looking at, and renders
 * nothing in production. Reads the public `NEXT_PUBLIC_APP_STAGE` value, so it is
 * safe to use in both server and client components.
 */

type Stage = 'development' | 'test'

const STAGE_CONFIG: Record<Stage, { label: string; className: string }> = {
    development: {
        label: 'DEV',
        // amber — warm, clearly "not real"
        className: 'bg-amber-100 text-amber-800 border-amber-300'
    },
    test: {
        label: 'TEST',
        // violet — distinct from dev at a glance
        className: 'bg-violet-100 text-violet-800 border-violet-300'
    }
}

export function EnvPill({ className }: { className?: string }) {
    const stage = process.env.NEXT_PUBLIC_APP_STAGE

    if (stage !== 'development' && stage !== 'test') return null

    const { label, className: stageClassName } = STAGE_CONFIG[stage]

    return (
        <span
            aria-label={`Environment: ${label}`}
            className={cn(
                'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide leading-none select-none',
                stageClassName,
                className
            )}>
            {label}
        </span>
    )
}
